import React from 'react';
import { useTranslation } from '../../i18n';
import { renderAvatar } from '../../utils/avatarUtils';
import { TeamId, TeamsState } from '../../types/teams';

export interface TeamStandingRow {
  id: TeamId;
  name: string;
  score: number;
  members: Array<{ id: string; name: string; avatar?: string }>;
}

interface TeamStandingsProps {
  teams: TeamStandingRow[];
  isMobile?: boolean;
  accent?: string;
  variant?: 'default' | 'light-card';
  pointsGained?: Map<string, number>;
  showPointsGained?: Map<string, boolean>;
}

/** Build team rows from room teams + players (display score prefers teams.score, including 0). */
export function buildTeamStandingRows(
  players: Array<{ id: string; name: string; avatar?: string; score?: number; teamId?: TeamId }>,
  teams?: TeamsState
): TeamStandingRow[] {
  const ids: TeamId[] = ['A', 'B'];
  return ids.map((id) => {
    const members = players.filter((p) => p.teamId === id);
    const meta = teams?.[id];
    const memberSum = members.reduce((sum, p) => sum + (p.score || 0), 0);
    // Team meta is source of truth when present — including score 0 (do not fall back to memberSum).
    const score = meta != null ? meta.score : memberSum;
    return {
      id,
      name: meta?.name || (id === 'A' ? 'Team A' : 'Team B'),
      score,
      members: members.map((m) => ({ id: m.id, name: m.name, avatar: m.avatar })),
    };
  });
}

/**
 * Team vs Team standings — reuses standings visual language without forking leaderboard systems.
 */
export const TeamStandings: React.FC<TeamStandingsProps> = ({
  teams,
  isMobile = false,
  accent = '#F59E0B',
  variant = 'default',
  pointsGained = new Map(),
  showPointsGained = new Map(),
}) => {
  const { t } = useTranslation();
  const isLightCard = variant === 'light-card';
  const sorted = [...teams].sort((a, b) => b.score - a.score);
  const rowDivider = isLightCard ? '1px solid rgba(99, 102, 241, 0.10)' : '1px solid var(--border-light)';

  return (
    <div
      className={`standings-container ${isMobile ? 'standings-container-mobile' : ''}`}
      data-testid="team-standings"
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        background: isLightCard ? 'rgba(255, 255, 255, 0.10)' : undefined,
        borderRadius: isLightCard ? '16px' : undefined,
      }}
    >
      <h3
        style={{
          margin: '0 0 0.5rem',
          fontSize: isMobile ? '0.95rem' : '1.05rem',
          color: '#FFFFFF',
          fontWeight: 700,
        }}
      >
        {t('lobby.teams.title')}
      </h3>
      {sorted.map((team, index) => (
        <div
          key={team.id}
          className="standings-player-item standings-player-item-desktop"
          data-testid="team-standings-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            padding: '0.55rem 0.5rem',
            borderBottom: index < sorted.length - 1 ? rowDivider : 'none',
            borderLeft: `3px solid ${team.id === 'A' ? 'rgba(34, 197, 94, 0.75)' : 'rgba(59, 130, 246, 0.75)'}`,
            marginBottom: index < sorted.length - 1 ? '0.22rem' : undefined,
            borderRadius: isLightCard ? '12px' : undefined,
            background: 'rgba(255, 255, 255, 0.04)',
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0, flex: 1 }}>
            <span style={{ fontWeight: 700, color: '#fff', fontSize: isMobile ? '0.9rem' : '1rem' }}>
              {team.name}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', flexWrap: 'wrap' }}>
              {team.members.slice(0, 6).map((m) => (
                <span key={m.id} title={m.name} style={{ display: 'inline-flex' }}>
                  {renderAvatar(m.avatar, '1.25rem')}
                </span>
              ))}
              {team.members.length > 6 && (
                <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>+{team.members.length - 6}</span>
              )}
              {team.members.length === 0 && (
                <span style={{ fontSize: '0.75rem', opacity: 0.5 }}>{t('lobby.teams.empty')}</span>
              )}
            </span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
            {showPointsGained.get(team.id) && pointsGained.get(team.id) ? (
              <span className="standings-points-gained" style={{ color: accent, fontWeight: 800 }}>
                +{pointsGained.get(team.id)}{t('common.pts')}
              </span>
            ) : null}
            <span className="standings-score" style={{ fontWeight: 800, minWidth: '3rem', textAlign: 'center' }}>
              {team.score}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};
